import { onMounted, onUnmounted } from 'vue';
import { useRouter, useNuxtApp } from '#imports';
import { useAuthClient } from './useAuthClient';
import { primeSessionCache } from '../../utils/sessionCache';
import { createLogger } from '../../utils/logger';

const log = createLogger('GoogleOneTap', { enabled: false });

/**
 * Triggers Google One Tap prompt on the current page.
 * Call this in login/register pages or any guest-accessible page.
 *
 * @param callbackPath - Where to redirect after successful sign-in (default: '/')
 */
export function useGoogleOneTap(callbackPath = '/') {
  const authClient = useAuthClient();
  const router = useRouter();
  const { $refreshConvexAuth } = useNuxtApp();

  // Type alias for the oneTap method exposed by oneTapClient plugin.
  // Better Auth merges plugin methods onto the client; TypeScript doesn't always
  // resolve this through conditional plugin spreads, so we narrow with a runtime guard.
  type OneTapClient = {
    oneTap: (opts: {
      fetchOptions?: {
        onSuccess?: () => void;
        onError?: (ctx: { error: { message: string } }) => void;
      };
      onPromptNotification?: (notification: unknown) => void;
    }) => Promise<void>;
  };

  let isMounted = true;
  onMounted(async () => {
    if (import.meta.server) return;

    // Runtime guard: only proceed if the client was configured with oneTapClient.
    if (!('oneTap' in authClient)) {
      log.debug('Auth client has no oneTap — oneTapClient plugin not included');
      return;
    }

    const client = authClient as typeof authClient & OneTapClient;

    try {
      await client.oneTap({
        fetchOptions: {
          onSuccess: async () => {
            if (!isMounted) return;
            log.info('One Tap sign-in successful, navigating to', callbackPath);

            // Prime the session cache so middleware gets an instant hit
            try {
              const freshSession = await authClient.getSession();
              if (freshSession?.data) {
                primeSessionCache(freshSession as Parameters<typeof primeSessionCache>[0]);
              }
            } catch { /* non-fatal */ }

            // Refresh the Convex auth token
            if (typeof $refreshConvexAuth === 'function') {
              ($refreshConvexAuth as () => Promise<void>)().catch(() => {});
            }

            router.push(callbackPath);
          },
          onError: (ctx: { error: { message: string } }) => {
            log.warn('One Tap sign-in failed:', ctx.error.message);
          },
        },
        onPromptNotification: (notification: unknown) => {
          log.debug('One Tap prompt dismissed/skipped', notification);
        },
      });
    } catch (error) {
      log.debug('One Tap not available or blocked', error);
    }
  });
  onUnmounted(() => {
    isMounted = false;
  });
}
